// src/components/register/RegistrationSuccess.tsx
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { db } from '../../config/firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

interface RegistrationResult {
  agencyId: string;
  shortTokenId: string;
  urls: {
    lp: string;
    simulator: string;
  };
}

export const RegistrationSuccess = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchRegistrationResult = async () => {
      try {
        // pendingRegistrationsから完了した登録を取得
        // session_idがある場合は、最新の完了した登録を取得
        const pendingQuery = query(
          collection(db, 'pendingRegistrations'),
          where('status', '==', 'completed'),
          orderBy('completedAt', 'desc'),
          limit(1)
        );

        const snapshot = await getDocs(pendingQuery);

        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          const agencyId = data.agencyId;

          // agenciesコレクションから詳細情報を取得
          const agencyQuery = query(collection(db, 'agencies'), where('agencyId', '==', agencyId), limit(1));

          const agencySnapshot = await getDocs(agencyQuery);

          if (!agencySnapshot.empty) {
            const agencyData = agencySnapshot.docs[0].data();
            const tokenId = agencyData.lastShortTokenId;

            setRegistrationResult({
              agencyId: agencyId,
              shortTokenId: tokenId,
              urls: {
                lp: `https://ikiiki-smile-agency.jp/?id=${tokenId}`,
                simulator: `https://simulator.ikiiki-smile-agency.jp/access?id=${tokenId}`,
              },
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch registration result:', err);
        setError('登録情報の取得に失敗しました。メールをご確認ください。');
      } finally {
        setIsLoading(false);
      }
    };

    // 少し待ってからデータ取得（webhookの処理完了を待つ）
    const timer = setTimeout(() => {
      fetchRegistrationResult();
    }, 3000);

    return () => clearTimeout(timer);
  }, [sessionId]);

  // ローディング中
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">お支払いが完了しました！</h1>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center mb-2">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin mr-2" />
                <span className="text-sm font-medium text-blue-800">登録処理中...</span>
              </div>
              <p className="text-sm text-blue-700">しばらくお待ちください</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // エラー時
  if (error || !registrationResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">お支払いが完了しました！</h1>
            <p className="text-gray-600 mb-6">
              代理店登録が完了しました。
              <br />
              ご登録いただいたメールアドレスに詳細を送信しましたのでご確認ください。
            </p>
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>ご注意:</strong> メールが届かない場合は、迷惑メールフォルダをご確認ください。
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 登録完了画面（URL表示あり）
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* 完了メッセージ */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">登録が完了しました！</h1>
            <p className="text-gray-600">代理店専用のアクセスURLが発行されました。</p>
          </div>

          {/* LP URL */}
          <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">いきいきスマイル共済 LP</h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                readOnly
                value={registrationResult.urls.lp}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(registrationResult.urls.lp);
                  alert('LPのURLをコピーしました');
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
              >
                📋 コピー
              </button>
              <button
                onClick={() => window.open(registrationResult.urls.lp, '_blank')}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
              >
                🔗 開く
              </button>
            </div>
          </div>

          {/* シミュレーター URL */}
          <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">共済シミュレーター</h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                readOnly
                value={registrationResult.urls.simulator}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(registrationResult.urls.simulator);
                  alert('シミュレーターのURLをコピーしました');
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
              >
                📋 コピー
              </button>
              <button
                onClick={() => window.open(registrationResult.urls.simulator, '_blank')}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                🔗 開く
              </button>
            </div>
          </div>

          {/* 編集用URL */}
          <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">📝 登録情報の編集</h2>
            <p className="text-sm text-gray-600 mb-3">
              以下のURLから、いつでも登録情報を編集できます。 このURLをブックマークしておくことをお勧めします。
            </p>
            <div className="flex items-center gap-3 mb-3">
              <input
                type="text"
                readOnly
                value={`https://admin.ikiiki-smile-agency.jp/edit?id=${registrationResult.shortTokenId}`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `https://admin.ikiiki-smile-agency.jp/edit?id=${registrationResult.shortTokenId}`
                  );
                  alert('編集URLをコピーしました');
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
              >
                📋 コピー
              </button>
            </div>
            <a
              href={`/edit?id=${registrationResult.shortTokenId}`}
              className="inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              登録情報を編集する
            </a>
          </div>

          {/* 注意事項 */}
          <div className="p-6 bg-gray-50 border border-gray-200 rounded-md">
            <h3 className="text-md font-semibold text-gray-900 mb-3">重要なお知らせ</h3>
            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
              <li>このURLは代理店専用のアクセスURLです</li>
              <li>URLを大切に保管してください</li>
              <li>第三者に共有しないでください</li>
              <li>ご登録いただいたメールアドレスにもURLを送信しました</li>
            </ul>
          </div>

          {/* LPを確認ボタン */}
          <div className="mt-8 text-center">
            <button
              onClick={() => window.open(registrationResult.urls.lp, '_blank')}
              className="px-8 py-3 bg-yellow-500 text-white text-lg font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
            >
              あなたの LP を確認する →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
